import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CircuitRanking {
  id: string;
  organizer_id: string;
  sport: string;
  category: string;
  subcategory: string;
  total_points: number;
  tournaments_played: number;
  times_champion: number;
  times_subcampeon: number;
  times_sf: number;
  times_qf: number;
  position: number;
  organizer_name: string;
  organizer_avatar: string | null;
  circuit_name: string | null;
}

export const useUserCircuitRankings = (userId?: string) => {
  const [rankings, setRankings] = useState<CircuitRanking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchRankings = async () => {
      setLoading(true);
      try {
        // 1. Get user's leaderboard entries
        const { data: entries } = await supabase
          .from('ranking_circuit_leaderboard')
          .select('circuit_id, category, subcategory, total_points, tournaments_played, position, times_champion, times_subcampeon, times_sf, times_qf')
          .eq('user_id', userId);

        if (!entries?.length) {
          setRankings([]);
          setLoading(false);
          return;
        }

        const circuitIds = [...new Set(entries.map(e => e.circuit_id))];

        // 2. Get circuit names + sport, and organizer profiles in parallel
        const [circuitsRes, organizersRes] = await Promise.all([
          supabase
            .from('ranking_circuits')
            .select('id, name, sport')
            .in('id', circuitIds),
          supabase
            .from('organizer_public_profiles')
            .select('user_id, ranking_display_circuit_id, company_name, avatar_url')
            .in('ranking_display_circuit_id', circuitIds),
        ]);

        const circuitMap = new Map(
          (circuitsRes.data || []).map(c => [c.id, { name: c.name, sport: c.sport }])
        );

        const orgByCircuit = new Map(
          (organizersRes.data || []).map(o => [
            o.ranking_display_circuit_id!,
            { organizer_id: o.user_id!, name: o.company_name || 'Organizador', avatar: o.avatar_url },
          ])
        );

        // 3. Map entries to rankings
        const results: CircuitRanking[] = entries
          .map(entry => {
            const circuit = circuitMap.get(entry.circuit_id);
            const org = orgByCircuit.get(entry.circuit_id);
            if (!org) return null;

            return {
              id: `${entry.circuit_id}::${entry.category}::${entry.subcategory}`,
              organizer_id: org.organizer_id,
              sport: circuit?.sport || '',
              category: entry.category,
              subcategory: entry.subcategory,
              total_points: entry.total_points,
              tournaments_played: entry.tournaments_played,
              times_champion: entry.times_champion || 0,
              times_subcampeon: entry.times_subcampeon || 0,
              times_sf: entry.times_sf || 0,
              times_qf: entry.times_qf || 0,
              position: entry.position,
              organizer_name: org.name,
              organizer_avatar: org.avatar,
              circuit_name: circuit?.name || null,
            };
          })
          .filter(Boolean) as CircuitRanking[];

        setRankings(results);
      } catch (err) {
        console.error('Error fetching circuit rankings:', err);
        setRankings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRankings();
  }, [userId]);

  return { rankings, loading };
};
